import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, Moon, Sun } from 'lucide-react'
import { themeActions, useFollowsSystem, useThemeInfo } from '../design/theme-store'
import { cn } from '../design/cn'

/**
 * Light, dark, or whatever the operating system is using, as one control in the header.
 *
 * There is one look, so there is no style to pick: only the mode. "Match system" is the default and
 * follows the system as it changes; choosing Light or Dark pins that choice.
 */

const ITEM =
  'flex cursor-pointer items-center justify-between gap-lg rounded-md px-md py-sm text-sm ' +
  'text-fg outline-none transition-colors hover:bg-hover focus:bg-hover'

export function ThemeSwitcher() {
  const info = useThemeInfo()
  const followsSystem = useFollowsSystem()
  const dark = info.mode === 'dark'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          // The accessible name carries the current state, so the control announces it without
          // printing an internal id on screen.
          aria-label={`Appearance: ${info.modeName}${followsSystem ? ', matching the system' : ''}`}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'text-header-muted transition-colors hover:bg-header-hover hover:text-header-fg',
          )}
        >
          {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          <span className="sr-only" data-testid="active-theme">
            {info.id}
          </span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="min-w-52 rounded-lg border border-card-border bg-card p-xs shadow-lg"
          style={{ zIndex: 'var(--z-dropdown)' }}
        >
          <DropdownMenu.Label className="px-md py-xs text-xs font-medium text-fg-muted">
            Appearance
          </DropdownMenu.Label>
          <DropdownMenu.Item className={ITEM} onSelect={() => themeActions.setMode('light')}>
            Light
            {!followsSystem && !dark ? <Check size={14} aria-hidden="true" /> : null}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={ITEM} onSelect={() => themeActions.setMode('dark')}>
            Dark
            {!followsSystem && dark ? <Check size={14} aria-hidden="true" /> : null}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={ITEM} onSelect={() => themeActions.followSystem()}>
            Match system
            {followsSystem ? <Check size={14} aria-hidden="true" /> : null}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
